export function chunk<T>(array: T[], size: number): T[][] {
  const chunks: T[][] = []
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size))
  }
  return chunks
}

export function getRandomElementFromArray<T>(array: readonly T[]): T {
  return array[Math.floor(Math.random() * array.length)]
}

export function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

type Comparator<T> = (a: T, b: T) => boolean

export function uniqWith<T>(array: T[], comparator: Comparator<T>): T[] {
  if (!Array.isArray(array) || typeof comparator !== 'function') {
    throw new TypeError(
      'First argument must be an array and second argument must be a function',
    )
  }

  return array.reduce((unique: T[], current: T) => {
    // Check if current item matches any existing item in unique array based on comparator
    const hasDuplicate = unique.some(item => comparator(item, current))
    // If no duplicate found, add current item to unique array
    return hasDuplicate ? unique : [...unique, current]
  }, [])
}

export function compact<T>(
  array: (T | null | undefined)[] | null | undefined,
): Exclude<T, null | undefined>[] {
  const length = array === null || array === undefined ? 0 : array.length
  let index = -1
  let resIndex = 0
  const result: Exclude<T, null | undefined>[] = []

  while (index < length) {
    index += 1
    if (array !== null && array !== undefined) {
      const value = array[index]

      if (
        value !== null &&
        value !== undefined &&
        value !== false &&
        value !== 0 &&
        value !== ''
      ) {
        result[resIndex] = value as Exclude<T, null | undefined>
        resIndex += 1
      }
    }
  }

  return result
}

export function sumBy<T>(
  array: T[] | null | undefined,
  iteratee: ((value: T) => number) | keyof T,
): number {
  if (!array || !array.length) {
    return 0
  }

  const getValue =
    typeof iteratee === 'function'
      ? iteratee
      : (item: T) => item[iteratee] as unknown as number

  let result: number | undefined = undefined
  for (const item of array) {
    const current = getValue(item)
    if (current !== undefined) {
      result = result === undefined ? current : result + current
    }
  }

  return result ?? 0
}

type Iteratee<T> = ((item: T) => unknown) | keyof T
type SortOrder = 'asc' | 'desc'

export function sortBy<T>(
  collection: T[] | Record<string, T> | null | undefined,
  iteratees?: Iteratee<T> | Iteratee<T>[] | string,
  order?: SortOrder | SortOrder[],
): T[] {
  // Handle null or undefined inputs
  if (collection === null || collection === undefined) {
    return []
  }

  // Initialize array on declaration, converting object to array of values if needed
  const array: T[] = Array.isArray(collection)
    ? collection
    : Object.values(collection)

  // Ensure array is valid
  if (!Array.isArray(array)) {
    return []
  }

  // If no iteratees provided, sort by identity (default behavior)
  if (iteratees === undefined) {
    return [...array].sort((a, b) => {
      if (a < b) {
        return -1
      }
      if (a > b) {
        return 1
      }
      return 0
    })
  }

  // Normalize iteratees to an array
  let normalizedIteratees: Iteratee<T>[] = []
  if (Array.isArray(iteratees)) {
    normalizedIteratees = iteratees
  } else if (iteratees !== undefined) {
    normalizedIteratees = [iteratees as Iteratee<T>]
  }

  // Normalize orders to an array
  let orderArray: SortOrder[] = []
  if (Array.isArray(order)) {
    orderArray = order
  } else if (order === 'asc' || order === 'desc') {
    orderArray = [order]
  } else {
    orderArray = normalizedIteratees.map(() => 'asc')
  }

  // If no iteratees, use identity
  if (normalizedIteratees.length === 0) {
    return [...array].sort((a, b) => {
      if (a < b) {
        return -1
      }
      if (a > b) {
        return 1
      }
      return 0
    })
  }

  return [...array].sort((a, b) => {
    let result = 0
    for (let i = 0; i < normalizedIteratees.length; i++) {
      const iteratee = normalizedIteratees[i]
      const isAsc = orderArray[i] !== 'desc'

      // Handle string iteratee (e.g., for changeCase)
      const resolvedIteratee =
        typeof iteratee === 'string'
          ? (item: T) => item[iteratee as keyof T]
          : iteratee

      const valueA =
        typeof resolvedIteratee === 'function'
          ? resolvedIteratee(a)
          : a[resolvedIteratee]
      const valueB =
        typeof resolvedIteratee === 'function'
          ? resolvedIteratee(b)
          : b[resolvedIteratee]

      if (valueA === valueB) {
        if (i === normalizedIteratees.length - 1) {
          result = 0
          break
        }
      } else if (valueA === null || valueA === undefined) {
        result = isAsc ? 1 : -1
        break
      } else if (valueB === null || valueB === undefined) {
        result = isAsc ? -1 : 1
        break
      } else if (valueA < valueB) {
        if (isAsc) {
          result = -1
        } else {
          result = 1
        }
        break
      } else {
        if (isAsc) {
          result = 1
        } else {
          result = -1
        }
        break
      }
    }
    return result
  })
}
